/**
 * Email + password sign-in, for people without a Google account. Google
 * remains the primary path (`GoogleSignInButton` first) — this is the
 * fallback the same page offers underneath it.
 */

import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router'
import { useAuth } from '../auth/AuthContext'
import { GoogleSignInButton } from '../auth/GoogleSignInButton'
import { LangSwitch } from '../components/LangSwitch'
import { PivotLogo } from '../components/PivotLogo'
import { ThemeToggle } from '../components/ThemeToggle'
import { useI18n } from '../i18n'
import '../styles/auth-forms.css'
import '../styles/header-actions.css'

export default function Login() {
  const { t } = useI18n()
  const { user, loginWithPassword } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (user) return <Navigate to={user.profile_complete ? '/dashboard' : '/onboarding'} replace />

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const session = await loginWithPassword(email, password)
      // Where they actually belong: a user who never finished onboarding gets
      // sent there directly, rather than to a dashboard the guard bounces.
      navigate(session.profile_complete ? '/dashboard' : '/onboarding')
    } catch {
      setError(t('auth.genericError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="auth-page">
      <title>{`${t('auth.loginTitle')} — ${t('common.appName')}`}</title>
      <header className="auth-bar">
        <Link to="/" className="auth-brand" aria-label={t('a11y.authHomeLink')}>
          <PivotLogo />
          {t('common.appName')}
        </Link>
        <div className="header-actions">
          <LangSwitch />
          <ThemeToggle />
        </div>
      </header>
      <div className="auth-body">
        <div className="auth-card">
          <h1>{t('auth.loginTitle')}</h1>
          <GoogleSignInButton />
          <div className="auth-divider">{t('auth.orDivider')}</div>
          <form onSubmit={handleSubmit} className="stack auth-form">
            <label>
              {t('auth.emailLabel')}
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
              />
            </label>
            <label>
              {t('auth.passwordLabel')}
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="current-password"
              />
            </label>
            {error && <p className="form-error">{error}</p>}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {t('auth.loginCta')}
            </button>
          </form>
          <div className="auth-links">
            <Link to="/forgot-password" className="auth-link">
              {t('auth.loginForgot')}
            </Link>
            <p>
              {t('auth.loginNoAccount')}{' '}
              <Link to="/signup" className="auth-link">
                {t('auth.loginCreateAccount')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
